import styled from '@emotion/styled'
import { ReactComponent as CloseIcon } from 'shop/assets/icons/close.svg'
import Theme, { StyledHTMLElement } from 'shop/theme/types'

export type modalButtonVariant =
  | 'sideModal'
  | 'newFulfillmentModal'
  | 'newModal'
  | 'bottomModal'
  | 'upsell'

interface Props {
  handleCloseModal?: () => void
  variant?: modalButtonVariant
}

const CloseButton = ({ variant, handleCloseModal }: Props) => {
  return (
    <Button
      variant={variant}
      data-testid='closeButton'
      onClick={handleCloseModal}
    >
      <CloseIcon />
    </Button>
  )
}

const sharedButtonStyles = {
  '&:hover': {
    outline: '1px solid white',
    backgroundColor: '#0000000d',
    borderRadius: '28px'
  },
  '&:active': {
    outline: '1px solid white',
    backgroundColor: '#0000001a',
    borderRadius: '28px'
  }
}

/** Store of style variants */
const variantStyles = {
  newFulfillmentModal: (theme: Theme) => ({
    height: '24px',
    width: '24px',
    position: 'absolute',
    top: '23px',
    right: '12px',
    ...sharedButtonStyles,
    '> svg': {
      width: '24px',
      height: '24px'
    },
    [theme.mediaQueries.viewport5]: {
      position: 'unset',
      top: '12px',
      right: '12px',
      height: '24px',
      width: '24px'
    }
  }),
  sideModal: (theme: Theme) => ({
    top: '24px',
    right: '16px',
    alignItems: 'center',
    ...sharedButtonStyles,
    [theme.mediaQueries.viewport5]: {
      top: '18px',
      right: '24px'
    },
    [theme.mediaQueries.viewport7]: {
      top: '24px',
      right: '16px'
    },
    '> svg': {
      [theme.mediaQueries.viewport7]: {
        width: '24px',
        height: '24px'
      },
      '> path': {
        fill: theme.colors.primary
      }
    }
  }),
  newModal: (theme: Theme) => ({
    height: '24px',
    width: '24px',
    top: '16px',
    right: '16px',
    ...sharedButtonStyles,

    '> svg': {
      width: '24px',
      height: '24px'
    },

    [theme.mediaQueries.viewport5]: {
      top: '24px',
      right: '24px'
    }
  }),
  bottomModal: (theme: Theme) => ({
    height: '24px',
    width: '24px',
    top: '18px',
    right: '16px',
    ...sharedButtonStyles,

    '> svg': {
      width: '24px',
      height: '24px'
    },

    [theme.mediaQueries.viewport5]: {
      top: '22px',
      right: '22px'
    }
  }),
  upsell: (theme: Theme) => ({
    height: '24px',
    width: '24px',
    top: '16px',
    right: '16px',
    ...sharedButtonStyles,

    '> svg': {
      width: '24px',
      height: '24px'
    },

    [theme.mediaQueries.viewport7]: {
      top: '16px',
      right: '16px'
    }
  })
}

/** Accesses store of style variants and returns variant based on variant name provided by prop */
const returnVariantStyle = (theme: Theme, variant: string): any =>
  (variant && variantStyles[variant](theme)) || {}

const Button = styled.div<StyledHTMLElement & { variant?: modalButtonVariant }>(
  ({ variant, theme }) => ({
    zIndex: 1000,
    position: 'absolute',
    height: '36px',
    width: '36px',
    top: '8px',
    right: '8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      '> svg': {
        opacity: 0.4,
        transition: 'opacity 200ms ease-in'
      }
    },
    '> svg': {
      height: '18px',
      width: '18px',
      '> path': {
        fill: theme.colors.primary
      }
    },
    [theme.mediaQueries.viewport5]: {
      right: '16px',
      top: '16px'
    },
    [theme.mediaQueries.viewport7]: {
      top: '24px',
      right: '24px'
    },
    ...(variant && returnVariantStyle(theme, variant))
  })
)

export default CloseButton
